import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import RestoreFromTrashOutlinedIcon from '@mui/icons-material/RestoreFromTrashOutlined';
import _ from 'lodash';
import { useSearchParams } from 'react-router-dom';
import {
  ActionIconButton,
  PhoneField,
  PHONE_BLOCKING_STATUSES,
  ResponsiveDataTable,
  SaveButton,
  StandardFiltersBar,
  StandardPageHeader,
  StandardSearchField,
} from 'app/shared/ui';
import CustomPagination from 'app/widgets/Shared/Pagination';
import superadminServices from 'app/services/superadmin-services';
import { whatsappVerificationHelper } from 'app/utils/phone';

const USER_STATUS_OPTIONS = [
  { value: 'active', label: 'Activos' },
  { value: 'inactive', label: 'Inactivos' },
  { value: 'archived', label: 'Archivados' },
];
const USER_STATUS_VALUES = USER_STATUS_OPTIONS.map((option) => option.value);

const normalizeStatusFilter = (value) => (USER_STATUS_VALUES.includes(value) ? value : 'active');

const EMPTY_FORM = {
  id: null,
  first_name: '',
  last_name: '',
  email: '',
  code: '',
  tin: '',
  phone_primary: '',
  phone_secondary: '',
  active: true,
  archived: false,
  current_company_id: '',
  company_roles: [],
};

const createRoleRow = (row = {}) => ({
  uid: `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
  company_id: row.company_id ? `${row.company_id}` : '',
  company_name: row.company_name || '',
  company_archived: !!row.company_archived,
  profile_names: _.uniq((row.profile_names || []).map((name) => `${name}`)),
});

const formatLastSignIn = (value) => {
  if (!value) return '-';
  const dateValue = new Date(value);
  if (Number.isNaN(dateValue.getTime())) return '-';
  return dateValue.toLocaleString('es-CL');
};

const UsersIndex = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [records, setRecords] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [profileNames, setProfileNames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState(searchParams.get('company_id') || '');
  const [statusFilter, setStatusFilter] = useState(normalizeStatusFilter(searchParams.get('status')));

  const [openForm, setOpenForm] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [phoneChecks, setPhoneChecks] = useState({});
  // Mientras Twilio verifica un teléfono, o si dijo que no es válido, no se guarda.
  const phonesBlocked = _.some(phoneChecks, (status) => PHONE_BLOCKING_STATUSES.includes(status));

  const profileNameByCode = useMemo(
    () => _.fromPairs((profileNames || []).map((profile) => [profile.name, profile.human_name])),
    [profileNames],
  );

  const selectedCompany = useMemo(
    () => _.find(companies, (company) => `${company.id}` === `${selectedCompanyId}`) || null,
    [companies, selectedCompanyId],
  );

  const tableParams = useMemo(() => {
    const parts = [
      `page=${currentPage}`,
      `status=${statusFilter}`,
      encodeURI('q[s][]=first_name asc'),
    ];

    if (selectedCompanyId) {
      parts.push(encodeURI(`q[profiles_company_id_eq]=${selectedCompanyId}`));
    }

    if (searchQuery.trim()) {
      parts.push(encodeURI(`q[first_name_or_last_name_or_email_or_code_cont]=${searchQuery.trim()}`));
    }

    return `?${parts.join('&')}`;
  }, [currentPage, selectedCompanyId, searchQuery, statusFilter]);

  const loadEditOptions = () => {
    superadminServices.getUsersEditOptions()
      .then((resp) => {
        setProfileNames(resp?.profile_names || []);
        setCompanies(resp?.companies || []);
      })
      .catch(() => {
        setNotice({ severity: 'error', text: 'No se pudieron cargar opciones de edición.' });
      });
  };

  const loadRecords = useCallback(() => {
    setLoading(true);
    superadminServices.getUsers(tableParams)
      .then((resp) => {
        setRecords(resp?.data || []);
        setTotalPages(resp?.meta?.total_pages || 1);
      })
      .catch(() => setNotice({ severity: 'error', text: 'No se pudieron cargar usuarios.' }))
      .finally(() => setLoading(false));
  }, [tableParams]);

  useEffect(() => {
    loadEditOptions();
  }, []);

  useEffect(() => {
    const companyIdFromQuery = searchParams.get('company_id') || '';
    const statusFromQuery = normalizeStatusFilter(searchParams.get('status'));
    setSelectedCompanyId((prev) => (`${prev}` === `${companyIdFromQuery}` ? prev : companyIdFromQuery));
    setStatusFilter((prev) => (`${prev}` === `${statusFromQuery}` ? prev : statusFromQuery));
  }, [searchParams]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const openEdit = (record) => {
    if (record?.archived) {
      setNotice({ severity: 'warning', text: 'Debes restaurar el usuario antes de editarlo.' });
      return;
    }

    setLoadingForm(true);
    setOpenForm(true);
    superadminServices.getUser(record.id)
      .then((resp) => {
        setForm({
          id: resp.id,
          first_name: resp.first_name || '',
          last_name: resp.last_name || '',
          email: resp.email || '',
          code: resp.code || '',
          tin: resp.tin || '',
          phone_primary: resp.phone_primary || '',
          phone_secondary: resp.phone_secondary || '',
          whatsapp_verified_phone: resp.whatsapp_verified_phone || '',
          active: !!resp.active,
          archived: !!resp.archived,
          current_company_id: resp.current_company_id ? `${resp.current_company_id}` : '',
          company_roles: _.map(resp.roles_by_company || [], (row) => createRoleRow(row)),
        });
      })
      .catch(() => {
        setNotice({ severity: 'error', text: 'No se pudo cargar el detalle del usuario.' });
        setOpenForm(false);
      })
      .finally(() => setLoadingForm(false));
  };

  const closeForm = (force = false) => {
    if (saving && !force) return;
    setOpenForm(false);
    setForm(EMPTY_FORM);
  };

  const addCompanyRoleRow = () => {
    setForm((prev) => ({ ...prev, company_roles: [...prev.company_roles, createRoleRow()] }));
  };

  const removeCompanyRoleRow = (uid) => {
    setForm((prev) => ({ ...prev, company_roles: prev.company_roles.filter((row) => row.uid !== uid) }));
  };

  const updateCompanyRoleRow = (uid, changes) => {
    setForm((prev) => ({
      ...prev,
      company_roles: prev.company_roles.map((row) => (row.uid === uid ? { ...row, ...changes } : row)),
    }));
  };

  const roleCompanies = useMemo(() => {
    const merged = {};
    (companies || []).forEach((company) => {
      merged[`${company.id}`] = {
        id: `${company.id}`,
        name: company.name,
        archived_at: company.archived_at || null,
      };
    });

    (form.company_roles || []).forEach((row) => {
      if (!row.company_id) return;
      if (merged[row.company_id]) return;
      merged[row.company_id] = {
        id: `${row.company_id}`,
        name: row.company_name || `Empresa #${row.company_id}`,
        archived_at: row.company_archived ? new Date().toISOString() : null,
      };
    });

    return _.sortBy(Object.values(merged), (company) => company.name?.toLowerCase?.() || '');
  }, [companies, form.company_roles]);

  // Todos los perfiles sirven en cualquier empresa.
  const allowedProfilesForCompany = useCallback(() => profileNames, [profileNames]);

  const sanitizeRoleNamesForCompany = useCallback((companyId, roleNames) => {
    const allowedProfileNames = allowedProfilesForCompany(companyId).map((profile) => `${profile.name}`);
    return _.uniq(
      (roleNames || [])
        .map((name) => `${name}`)
        .filter((name) => allowedProfileNames.includes(name)),
    );
  }, [allowedProfilesForCompany]);

  useEffect(() => {
    if (!openForm) return;

    setForm((prev) => {
      const nextCompanyRoles = (prev.company_roles || []).map((row) => {
        const nextProfileNames = sanitizeRoleNamesForCompany(row.company_id, row.profile_names);
        if (_.isEqual(nextProfileNames, row.profile_names || [])) return row;
        return { ...row, profile_names: nextProfileNames };
      });

      const rolesChanged = !_.isEqual(nextCompanyRoles, prev.company_roles || []);
      if (!rolesChanged) return prev;

      const hasCurrentCompanyRole = prev.current_company_id
        ? _.some(nextCompanyRoles, (row) => `${row.company_id}` === `${prev.current_company_id}` && row.profile_names.length > 0)
        : true;

      return {
        ...prev,
        company_roles: nextCompanyRoles,
        current_company_id: hasCurrentCompanyRole ? prev.current_company_id : '',
      };
    });
  }, [openForm, sanitizeRoleNamesForCompany]);

  const saveUser = () => {
    if (!form.id) return;

    const normalizedRoles = _.chain(form.company_roles || [])
      .map((row) => ({
        company_id: row.company_id ? Number.parseInt(row.company_id, 10) : null,
        profile_names: sanitizeRoleNamesForCompany(
          row.company_id,
          _.uniq((row.profile_names || []).filter(Boolean)),
        ),
      }))
      .filter((row) => row.company_id)
      .map((row) => ({
        company_id: row.company_id,
        profile_names: row.profile_names,
      }))
      .value();

    if (form.current_company_id && !_.some(normalizedRoles, (row) => `${row.company_id}` === `${form.current_company_id}`)) {
      setNotice({ severity: 'error', text: 'La empresa actual debe estar dentro de las empresas asignadas.' });
      return;
    }

    setSaving(true);
    superadminServices.updateUser(form.id, {
      user: {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        code: form.code,
        tin: form.tin,
        phone_primary: form.phone_primary || null,
        phone_secondary: form.phone_secondary || null,
        active: form.active,
        current_company_id: form.current_company_id || null,
        company_roles: normalizedRoles,
      },
    })
      .then((resp) => {
        setRecords((prev) => prev.map((row) => (row.id === resp.id ? resp : row)));
        setNotice({ severity: 'success', text: 'Usuario actualizado.' });
        closeForm(true);
      })
      .catch((error) => {
        const msg = error?.response?.data?.errors?.join(', ') || 'No se pudo guardar el usuario.';
        setNotice({ severity: 'error', text: msg });
      })
      .finally(() => setSaving(false));
  };

  const archiveUser = (record) => {
    if (!record?.id) return;
    if (!window.confirm(`¿Archivar a ${record.email || 'este usuario'}? Deberás restaurarlo antes de editarlo nuevamente.`)) return;

    setSaving(true);
    superadminServices.archiveUser(record.id)
      .then(() => {
        setNotice({ severity: 'success', text: 'Usuario archivado.' });
        loadRecords();
      })
      .catch((error) => {
        const msg = error?.response?.data?.errors?.join(', ') || 'No se pudo archivar el usuario.';
        setNotice({ severity: 'error', text: msg });
      })
      .finally(() => setSaving(false));
  };

  const restoreUser = (record) => {
    if (!record?.id) return;

    setSaving(true);
    superadminServices.restoreUser(record.id)
      .then(() => {
        setNotice({ severity: 'success', text: 'Usuario restaurado.' });
        loadRecords();
      })
      .catch((error) => {
        const msg = error?.response?.data?.errors?.join(', ') || 'No se pudo restaurar el usuario.';
        setNotice({ severity: 'error', text: msg });
      })
      .finally(() => setSaving(false));
  };

  const renderStatusChip = (record) => {
    if (record?.archived) return <Chip size="small" color="default" label="Archivado" />;
    if (record?.active) return <Chip size="small" color="success" label="Activo" />;
    return <Chip size="small" color="warning" label="Inactivo" />;
  };

  const renderRowActions = (record) => {
    if (record?.archived) {
      return (
        <ActionIconButton size="small" title="Restaurar usuario" onClick={() => restoreUser(record)} disabled={saving}>
          <RestoreFromTrashOutlinedIcon fontSize="small" />
        </ActionIconButton>
      );
    }

    return (
      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
        <ActionIconButton size="small" title="Editar usuario" onClick={() => openEdit(record)}>
          <EditOutlinedIcon fontSize="small" />
        </ActionIconButton>
        <ActionIconButton size="small" title="Archivar usuario" onClick={() => archiveUser(record)} disabled={saving}>
          <ArchiveOutlinedIcon fontSize="small" />
        </ActionIconButton>
      </Stack>
    );
  };

  const renderCompanyRoles = (record) => {
    const rows = record?.roles_by_company || [];
    if (rows.length === 0) return '-';

    return (
      <Stack spacing={0.5}>
        {rows.map((row) => {
          const isCurrent = `${record.current_company_id || ''}` === `${row.company_id}`;
          const roleLabels = (row.profile_names || []).map((profileName) => profileNameByCode[profileName] || profileName);

          return (
            <Stack
              key={`${record.id}_${row.company_id}`}
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{
                px: 0.75,
                py: 0.35,
                borderRadius: 1,
                border: '1px solid',
                borderColor: isCurrent ? 'primary.main' : 'transparent',
                backgroundColor: isCurrent ? 'action.hover' : 'transparent',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: isCurrent ? 600 : 400 }}>
                {(row.company_name || `Empresa #${row.company_id}`)}
                {' - '}
                {roleLabels.length ? roleLabels.join(', ') : 'Sin roles'}
              </Typography>
              {isCurrent && <Chip size="small" color="primary" label="Actual" />}
            </Stack>
          );
        })}
      </Stack>
    );
  };

  const columns = [
    {
      key: 'full_name',
      label: 'Nombre y apellido',
      render: (record) => (
        record.archived ? (
          <Typography variant="body2">
            {_.compact([record.first_name, record.last_name]).join(' ') || '-'}
          </Typography>
        ) : (
          <Button
            size="small"
            variant="text"
            sx={{ textTransform: 'none', px: 0, justifyContent: 'flex-start' }}
            onClick={() => openEdit(record)}
          >
            {_.compact([record.first_name, record.last_name]).join(' ') || '-'}
          </Button>
        )
      ),
    },
    {
      key: 'email',
      label: 'Correo',
      render: (record) => record.email || '-',
    },
    {
      key: 'last_sign_in_at',
      label: 'Último inicio de sesión',
      render: (record) => formatLastSignIn(record.last_sign_in_at),
    },
    {
      key: 'sign_in_count',
      label: 'Inicios de sesión',
      render: (record) => record.sign_in_count || 0,
    },
    {
      key: 'status',
      label: 'Estado',
      render: (record) => renderStatusChip(record),
    },
    {
      key: 'roles_by_company',
      label: 'Empresas y roles',
      render: (record) => renderCompanyRoles(record),
    },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'right',
      mobileHidden: true,
      render: (record) => renderRowActions(record),
    },
  ];

  return (
    <Box>
      <StandardPageHeader title="Superadministración · Usuarios" />

      <StandardFiltersBar>
        <Grid container spacing={1.25} alignItems="center">
          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={companies}
              getOptionLabel={(option) => option?.name || ''}
              value={selectedCompany}
              onChange={(e, value) => {
                const nextCompanyId = value?.id ? `${value.id}` : '';
                setCurrentPage(1);
                setSelectedCompanyId(nextCompanyId);
                setSearchParams((prevParams) => {
                  const nextParams = new URLSearchParams(prevParams);
                  if (nextCompanyId) {
                    nextParams.set('company_id', nextCompanyId);
                  } else {
                    nextParams.delete('company_id');
                  }
                  return nextParams;
                }, { replace: true });
              }}
              renderInput={(params) => <TextField {...params} label="Filtrar por empresa" />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ButtonGroup size="small" fullWidth>
              {USER_STATUS_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  variant={statusFilter === option.value ? 'contained' : 'outlined'}
                  onClick={() => {
                    setCurrentPage(1);
                    setStatusFilter(option.value);
                    setSearchParams((prevParams) => {
                      const nextParams = new URLSearchParams(prevParams);
                      if (option.value === 'active') {
                        nextParams.delete('status');
                      } else {
                        nextParams.set('status', option.value);
                      }
                      return nextParams;
                    }, { replace: true });
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </ButtonGroup>
          </Grid>
          <Grid item xs={12} md={5}>
            <StandardSearchField
              fullWidth
              placeholder="Buscar por nombre, apellido, correo o código"
              onChange={(e) => {
                setCurrentPage(1);
                setSearchQuery(e.target.value || '');
              }}
              onEnter={(value) => {
                setCurrentPage(1);
                setSearchQuery(value || '');
              }}
            />
          </Grid>
        </Grid>
      </StandardFiltersBar>

      {!!notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <ResponsiveDataTable
            columns={columns}
            rows={records}
            rowKey="id"
            emptyMessage="No hay usuarios para mostrar."
            mobileTitle={(record) => _.compact([record.first_name, record.last_name]).join(' ') || record.email || '-'}
            mobileSubtitle={(record) => `${record.email || '-'} · ${record.archived ? 'Archivado' : (record.active ? 'Activo' : 'Inactivo')}`}
            renderRowActions={(record) => renderRowActions(record)}
          />
        )}
      </Paper>

      <CustomPagination currentPage={currentPage} totalPages={totalPages} onPageSelect={setCurrentPage} />

      <Dialog open={openForm} onClose={closeForm} fullWidth maxWidth="md">
        <DialogTitle>Editar usuario</DialogTitle>
        <DialogContent>
          {loadingForm ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ pt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nombre"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Apellido"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Correo"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Código"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="RUT / TIN"
                  value={form.tin}
                  onChange={(e) => setForm({ ...form, tin: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={!!form.active}
                      onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    />
                  )}
                  label={form.active ? 'Activo (puede iniciar sesión)' : 'Inactivo (sin acceso)'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PhoneField
                  fullWidth
                  label="Teléfono primario"
                  value={form.phone_primary}
                  helperText={form.id ? whatsappVerificationHelper(form.phone_primary, form.whatsapp_verified_phone) : undefined}
                  onChange={(phone) => setForm((current) => ({ ...current, phone_primary: phone }))}
                  onStatusChange={(status) => setPhoneChecks((checks) => ({ ...checks, phone_primary: status }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PhoneField
                  fullWidth
                  label="Teléfono secundario"
                  value={form.phone_secondary}
                  onChange={(phone) => setForm((current) => ({ ...current, phone_secondary: phone }))}
                  onStatusChange={(status) => setPhoneChecks((checks) => ({ ...checks, phone_secondary: status }))}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={_.filter(roleCompanies, (company) => (
                    _.some(form.company_roles, (row) => `${row.company_id}` === `${company.id}` && row.profile_names.length > 0)
                  ))}
                  getOptionLabel={(option) => option?.name || ''}
                  value={_.find(roleCompanies, (company) => `${company.id}` === `${form.current_company_id}`) || null}
                  onChange={(e, value) => setForm({ ...form, current_company_id: value?.id || '' })}
                  renderInput={(params) => <TextField {...params} label="Empresa actual (current_company_id)" />}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h6">Empresas y roles</Typography>
                  <Button size="small" startIcon={<AddCircleOutlineOutlinedIcon />} onClick={addCompanyRoleRow}>
                    Agregar empresa
                  </Button>
                </Stack>
                <Stack spacing={1}>
                  {form.company_roles.map((row) => (
                    <Paper key={row.uid} variant="outlined" sx={{ p: 1 }}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} md={4}>
                          <Autocomplete
                            options={roleCompanies}
                            getOptionLabel={(option) => option?.name || ''}
                            value={_.find(roleCompanies, (company) => `${company.id}` === `${row.company_id}`) || null}
                            onChange={(e, value) => {
                              const nextCompanyId = value?.id || '';
                              updateCompanyRoleRow(row.uid, {
                                company_id: nextCompanyId,
                                company_name: value?.name || '',
                                company_archived: !!value?.archived_at,
                                profile_names: sanitizeRoleNamesForCompany(nextCompanyId, row.profile_names),
                              });
                            }}
                            renderInput={(params) => <TextField {...params} label="Empresa" />}
                          />
                        </Grid>
                        <Grid item xs={12} md={7}>
                          <Autocomplete
                            multiple
                            options={allowedProfilesForCompany(row.company_id)}
                            getOptionLabel={(option) => option?.human_name || option?.name || ''}
                            value={_.filter(
                              allowedProfilesForCompany(row.company_id),
                              (profile) => (row.profile_names || []).includes(profile.name),
                            )}
                            onChange={(e, values) => {
                              updateCompanyRoleRow(
                                row.uid,
                                { profile_names: values.map((value) => value.name) },
                              );
                            }}
                            renderInput={(params) => <TextField {...params} label="Roles" />}
                          />
                        </Grid>
                        <Grid item xs={12} md={1} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                          <ActionIconButton size="small" title="Eliminar empresa" onClick={() => removeCompanyRoleRow(row.uid)}>
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </ActionIconButton>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                  {form.company_roles.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Este usuario no tiene empresas asignadas.
                    </Typography>
                  )}
                </Stack>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm} disabled={saving}>Cancelar</Button>
          <SaveButton loading={saving} disabled={saving || loadingForm || phonesBlocked} onClick={saveUser}>
            Guardar
          </SaveButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersIndex;
